import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import { injectIntl, useIntl } from 'react-intl';
import isEqual from 'lodash-es/isEqual';

import intlShape from '@/shared/utils/intlShape';
import RelationSearch from '@/shared/components/input/RelationSearch';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import InvoiceScheduleTypeMultiSelect from '@/shared/components/input/multiselect/InvoiceScheduleTypeMultiSelect';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import userStorage from '@/shared/services/userStorage';

import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '@/shared/components/DynamicFilter';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import config from '../../../config';
import api from '../../../shared/api';

import messages from '../reportingMessages';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  invoiceStreamId: null,
  relationTypes: [],
  ownCompanies: [],
  legalEntities: [],
  assistant: null,
  responsible: null,
  associate: null,
  assistantBackup: null,
  billingUser: null,
  seniorUser: null,
  dateFrom: null,
  dateUntil: null,
  invoiceScheduleTypes: [], // Guid[]
  withLabels: [],
  withoutLabels: [],
  teamCodes: [],
  assignmentTemplateId: null,
  relationGroups: [],
  sectors: [],
};
const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters, 'invoiceScheduleTypes'),
];
const dynamicFiltersStorageKey = 'InvoicingHistoryReportDynamicFilters';

class InvoicingHistoryFilters extends Component {
  state = {
    invoicingTypes: [],
    dynamicFilters: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      invoiceStreamId: PropTypes.string,
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      dateUntil: momentPropTypes.momentObj,
      dateFrom: momentPropTypes.momentObj,
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      assistant: PropTypes.string,
      responsible: PropTypes.string,
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      teamCodes: PropTypes.arrayOf(PropTypes.number),
      invoiceScheduleTypes: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      assignmentTemplateId: PropTypes.string,
      sectors: PropTypes.arrayOf(PropTypes.number),
    }),
    intl: intlShape.isRequired,
  };
  static defaultProps = {
    filters: defaultFilters,
  };

  componentDidMount() {
    const { intl, filters } = this.props;

    const labelEndpoint = `${config.appUrls.api}/api/Resource/Tags`;
    Promise.all([api.base.get(labelEndpoint), enumCache.get('invoicingtype')]).then(
      ([{ data: labels }, invoicingtypes]) => {
        this.setState({
          invoicingTypes: [
            {
              key: -1,
              label: intl.formatMessage(messages.NoInvoicingSchedule),
            },
            ...invoicingtypes
              .map(x => ({
                key: x.key,
                label: intl.formatMessage(listItemMsg('invoicingtype', x.key, x.value)),
              }))
              .sort(dynamicSort('label')),
          ],
          labels: labels
            .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
            .sort(dynamicSort('sort')),
          dynamicFilters: initDynamicFilters(
            intl,
            allDynamicFilters,
            userStorage.getItem(dynamicFiltersStorageKey) || [],
            filters,
            defaultFilters,
          ),
        });
      },
    );
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };
  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.dateFrom === 'string') newFilters.dateFrom = moment.utc(filters.dateFrom);
    if (typeof newFilters.dateUntil === 'string')
      newFilters.dateUntil = moment.utc(filters.dateUntil);

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
  };
  dateChanged = (dateFrom, dateUntil) => {
    this.filterChanged({ dateFrom, dateUntil });
  };
  setYear = year => {
    if (!year || year <= 0) {
      this.dateChanged(null, null);
    } else {
      this.dateChanged(moment.utc([year, 0, 1]), moment.utc([year, 11, 31]));
    }
  };

  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null, invoiceStreamId: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
        invoiceStreamId: null,
      });
    }
  };
  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  render() {
    const { invoicingTypes, labels, dynamicFilters } = this.state;
    const { filters, intl } = this.props;
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    const selectedInvoicingType = filters.invoicingType
      ? invoicingTypes.find(x => x.key === filters.invoicingType)
      : null;
    const year = new Date().getFullYear();
    let selectedYear = !filters.dateFrom && !filters.dateUntil ? -1 : null;
    if (filters.dateFrom && filters.dateUntil) {
      if (
        moment(filters.dateFrom).isSame(
          moment(filters.dateFrom).startOf('year') &&
            moment(filters.dateUntil).isSame(moment(filters.dateUntil).endOf('year')),
        )
      ) {
        selectedYear = moment(filters.dateFrom).year;
      }
    }

    const emptyFrom = moment()
      .utc()
      .startOf('month')
      .subtract(1, 'year')
      .add(1, 'month');

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    return (
      <>
        <div className="row">
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.dateFrom}
                endDate={filters.dateUntil}
                onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.InvoiceDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.InvoiceDateUntil)}
                block
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <div role="group" aria-label="period" className="btn-group btn-group-justified">
                <button
                  onClick={() => this.setYear(-1)}
                  type="button"
                  className={classnames('btn btn-default', {
                    active: selectedYear === -1,
                  })}
                >
                  {emptyFrom.format('MM/YYYY')}
                  &rarr;
                  {moment().format('MM/YYYY')}
                </button>
                <button
                  onClick={() => this.setYear(year)}
                  type="button"
                  className={classnames('btn btn-default', {
                    active: selectedYear === year,
                  })}
                >
                  {year}
                </button>
                <button
                  onClick={() => this.setYear(year - 1)}
                  type="button"
                  className={classnames('btn btn-default', {
                    active: selectedYear === year - 1,
                  })}
                >
                  {year - 1}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <InvoiceScheduleTypeMultiSelect
                selection={filters.invoiceScheduleTypes}
                onChange={items => this.filterChanged({ invoiceScheduleTypes: items })}
                hideInvoicingVia
              />
            </div>
          </div>
          {visibleFilters && visibleFilters.length > 0
            ? visibleFilters.map(id => (
                <DynamicFilter
                  key={id}
                  type={id}
                  intl={intl}
                  labels={labels}
                  filters={filters}
                  filterChanged={this.filterChanged}
                  relationUserSearchParams={{ withUnassigned: false }}
                  labelsIdProp="id"
                />
              ))
            : null}
        </div>
        <div className="row">
          <div className="col-lg-4 col-md-6">
            <SavedFiltersButton
              screen="InvoicingHistoryReport"
              currentFilters={filters}
              defaultFilters={defaultFilters}
              onFiltersChanged={this.savedFilterChanged}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
          </div>
        </div>
      </>
    );
  }
}

export default injectIntl(InvoicingHistoryFilters);
